import type {
  EvCommand,
  EvIdentification,
  EvInvoiceStatus,
  EvPaymentStatus,
  EvSessionPhaseValue,
  EvTariffScope,
} from "@station/contracts";
import { keepPreviousData, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import { api } from "../../lib/api.js";
import { queryKeys } from "../../lib/query.js";
import { useFeatures, usePermissions } from "../../lib/session.js";
import { compact } from "../market/queries.js";
import { isCommandOpen, isSessionLive } from "./ev-values.js";

/**
 * Şarj ekranlarının sorguları (Prompt 19 web). Pano, ünite, oturum, komut, yük ve raporlar İSTASYON düzeyindedir (sorgu
 * anahtarında istasyon kimliği vardır); tarife ŞİRKET, dışa aktarım profili KİRACI düzeyindedir.
 *
 * İzinler (sunucu ile aynı): okuma `ev.read`; ünite kimlik bilgisi / tarife / istasyon ayarı `ev.manage` (tarife ve ayarda
 * sunucu `canEdit` döner); uzaktan başlat / durdur / takılı oturumu kapat / işletim komutları / şarj ağı içe aktarımı
 * `ev.operate`; yapılandırma değişikliği / yazılım / tanılama `ev.maintenance.manage` (adım yükseltme — sunucu
 * `REAUTHENTICATION_REQUIRED` döner, `useStepUp` pencereyi açar); raporlar ve dışa aktarım `ev.report.read`; dışa aktarım
 * profili yazımı KİRACI kapsamlı `ev.manage` (sunucu profil listesinde `canEdit` döner). Modül `EV` bayrağına bağlıdır:
 * kapalıysa ekranlar istek atmaz.
 *
 * **Tazeleme:** `ev.*` olayları (istasyon odası) modülün tüm önbelleğini geçersiz kılar (`lib/realtime.ts`); pano ve canlı
 * oturum ayrıca yoklanır (oda aboneliği kopabilir); mutasyonlar `useRefreshEv` ile `["ev"]`'yi tazeler.
 */
export interface EvPermissions {
  enabled: boolean;
  canRead: boolean;
  canManage: boolean;
  canOperate: boolean;
  canMaintain: boolean;
  canReadReports: boolean;
  /** Dışa aktarım profillerini görme: `ev.manage` ya da `ev.report.read` (yazma: profil listesinin `canEdit`'i). */
  canSeeExports: boolean;
  /** Oturumdaki satış / fatura / müşteri kartına bağlantı. */
  canOpenPosSale: boolean;
  canOpenInvoice: boolean;
  canOpenCustomer: boolean;
}

export function useEvPermissions(): EvPermissions {
  const permissions = usePermissions();
  const features = useFeatures();
  const canManage = permissions.has("ev.manage");
  const canReadReports = permissions.has("ev.report.read");
  return {
    enabled: features.has("EV"),
    canRead: permissions.has("ev.read"),
    canManage,
    canOperate: permissions.has("ev.operate"),
    canMaintain: permissions.has("ev.maintenance.manage"),
    canReadReports,
    canSeeExports: canManage || canReadReports,
    canOpenPosSale: permissions.has("pos.read") && features.has("LOCAL_POS"),
    canOpenInvoice: permissions.has("invoices.read") && features.has("E_DOCUMENTS"),
    canOpenCustomer: permissions.has("customers.read") && features.has("FLEET_LOYALTY"),
  };
}

export function useRefreshEv(): () => Promise<void> {
  const queryClient = useQueryClient();
  return useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: queryKeys.ev() });
  }, [queryClient]);
}

// ── Seçiciler ve istasyonlar ─────────────────────────────────────────────────
/**
 * Şarj iş birimi etkin, bayrağı açık ve kapsamdaki istasyonlar (sunucu süzer), bunların üniteleri + konnektörleri ve
 * elektrik sayaçları, tarife ürünleri.
 */
export function useEvPickers(enabled = true) {
  return useQuery({
    queryKey: queryKeys.ev("pickers"),
    enabled,
    staleTime: 300_000,
    queryFn: async () => (await api.GET("/ev/pickers")).data!,
  });
}

// ── Pano ve üniteler ─────────────────────────────────────────────────────────
/** Canlı pano: `ev.*` olaylarıyla tazelenir; ayrıca 30 sn'de bir (oda aboneliği kopabilir, süreler akar). */
export function useEvOverview(stationId: string | null) {
  return useQuery({
    queryKey: queryKeys.ev("overview", stationId),
    enabled: stationId !== null,
    refetchInterval: 30_000,
    queryFn: async () =>
      (await api.GET("/ev/overview", { params: { query: { stationId: stationId! } } })).data!,
  });
}

export function useEvChargers(stationId: string | null) {
  return useQuery({
    queryKey: queryKeys.ev("chargers", stationId),
    enabled: stationId !== null,
    refetchInterval: 60_000,
    queryFn: async () =>
      (
        await api.GET("/ev/chargers", {
          params: { query: { stationId: stationId!, includeUnmanaged: "true" } as never },
        })
      ).data!.items,
  });
}

/** Ünite ayrıntısı: sonuçlanmamış komut ya da canlı oturum varken 5 sn'de bir yoklanır. */
export function useEvCharger(id: string) {
  return useQuery({
    queryKey: queryKeys.ev("charger", id),
    refetchInterval: (query) => {
      const data = query.state.data;
      if (!data) return false;
      return data.recentCommands.some((command) => isCommandOpen(command.status)) ||
        data.liveSessions.length > 0
        ? 5000
        : 60_000;
    },
    queryFn: async () => (await api.GET("/ev/chargers/{id}", { params: { path: { id } } })).data!,
  });
}

export function useEvCommands(chargerId: string, cursor?: string) {
  const query = compact({ cursor, limit: 10 });
  return useQuery({
    queryKey: queryKeys.ev("commands", { chargerId, ...query }),
    placeholderData: keepPreviousData,
    refetchInterval: (state) =>
      state.state.data?.items.some((command: EvCommand) => isCommandOpen(command.status)) ? 3000 : false,
    queryFn: async () =>
      (
        await api.GET("/ev/chargers/{id}/commands", {
          params: { path: { id: chargerId }, query: query as never },
        })
      ).data!,
  });
}

// ── Oturumlar ────────────────────────────────────────────────────────────────
export interface SessionFilters {
  stationId: string;
  chargerId?: string | undefined;
  phase?: EvSessionPhaseValue | undefined;
  paymentStatus?: EvPaymentStatus | undefined;
  invoiceStatus?: EvInvoiceStatus | undefined;
  source?: "OCPP" | "PARTNER" | undefined;
  identification?: EvIdentification | undefined;
  from?: string | undefined;
  to?: string | undefined;
}

export function sessionsQuery(filters: SessionFilters, cursor?: string, limit = 25) {
  return compact({ ...filters, cursor, limit });
}

export function useEvSessions(filters: SessionFilters, cursor?: string) {
  const query = sessionsQuery(filters, cursor);
  return useQuery({
    queryKey: queryKeys.ev("sessions", query),
    placeholderData: keepPreviousData,
    queryFn: async () => (await api.GET("/ev/sessions", { params: { query: query as never } })).data!,
  });
}

/** Oturum ayrıntısı: canlıyken 15 sn'de bir yoklanır (ölçüm olayı dakikada bir gelir). */
export function useEvSession(id: string) {
  return useQuery({
    queryKey: queryKeys.ev("session", id),
    refetchInterval: (query) => (query.state.data && isSessionLive(query.state.data.phase) ? 15_000 : false),
    queryFn: async () => (await api.GET("/ev/sessions/{id}", { params: { path: { id } } })).data!,
  });
}

// ── Tarifeler ────────────────────────────────────────────────────────────────
export interface TariffFilters {
  companyId?: string | undefined;
  stationId?: string | undefined;
  scopeKind?: EvTariffScope | undefined;
  includeInactive?: boolean | undefined;
}

export function tariffsQuery(filters: TariffFilters, cursor?: string, limit = 50) {
  return compact({ ...filters, cursor, limit });
}

export function useEvTariffs(filters: TariffFilters, cursor?: string) {
  const query = tariffsQuery(filters, cursor);
  return useQuery({
    queryKey: queryKeys.ev("tariffs", query),
    placeholderData: keepPreviousData,
    queryFn: async () => (await api.GET("/ev/tariffs", { params: { query: query as never } })).data!,
  });
}

// ── İstasyon ayarı ve yük ────────────────────────────────────────────────────
export function useEvSettings(stationId: string | null) {
  return useQuery({
    queryKey: queryKeys.ev("settings", stationId),
    enabled: stationId !== null,
    queryFn: async () =>
      (await api.GET("/ev/stations/{stationId}/settings", { params: { path: { stationId: stationId! } } }))
        .data!,
  });
}

export function useEvLoad(stationId: string | null) {
  return useQuery({
    queryKey: queryKeys.ev("load", stationId),
    enabled: stationId !== null,
    refetchInterval: 30_000,
    queryFn: async () =>
      (
        await api.GET("/ev/stations/{stationId}/load", {
          params: { path: { stationId: stationId! }, query: { limit: 20 } as never },
        })
      ).data!,
  });
}

// ── Şarj ağı içe aktarımları ve dışa aktarım ─────────────────────────────────
export function importsQuery(stationId: string | undefined, cursor?: string, limit = 25) {
  return compact({ stationId, cursor, limit });
}

export function useEvPartnerImports(stationId: string | undefined, cursor?: string) {
  const query = importsQuery(stationId, cursor);
  return useQuery({
    queryKey: queryKeys.ev("partner-imports", query),
    placeholderData: keepPreviousData,
    queryFn: async () => (await api.GET("/ev/partner-imports", { params: { query: query as never } })).data!,
  });
}

export function useEvExportProfiles(enabled = true) {
  return useQuery({
    queryKey: queryKeys.ev("export-profiles"),
    enabled,
    queryFn: async () => (await api.GET("/ev/export-profiles")).data!,
  });
}

// ── Raporlar ─────────────────────────────────────────────────────────────────
export interface ReportRange {
  stationId: string | null;
  from: string;
  to: string;
}

export function useEvEnergyReport(range: ReportRange) {
  const query = compact({ ...range });
  return useQuery({
    queryKey: queryKeys.ev("energy", query),
    enabled: range.stationId !== null,
    placeholderData: keepPreviousData,
    queryFn: async () => (await api.GET("/ev/reports/energy", { params: { query: query as never } })).data!,
  });
}

export function useEvOccupancyReport(range: ReportRange) {
  const query = compact({ ...range });
  return useQuery({
    queryKey: queryKeys.ev("occupancy", query),
    enabled: range.stationId !== null,
    placeholderData: keepPreviousData,
    queryFn: async () =>
      (await api.GET("/ev/reports/occupancy", { params: { query: query as never } })).data!,
  });
}

export function useEvAvailabilityReport(range: ReportRange) {
  const query = compact({ ...range });
  return useQuery({
    queryKey: queryKeys.ev("availability", query),
    enabled: range.stationId !== null,
    placeholderData: keepPreviousData,
    queryFn: async () =>
      (await api.GET("/ev/reports/availability", { params: { query: query as never } })).data!,
  });
}

export function useEvCustomerSpendReport(range: ReportRange) {
  const query = compact({ ...range });
  return useQuery({
    queryKey: queryKeys.ev("customer-spend", query),
    enabled: range.stationId !== null,
    placeholderData: keepPreviousData,
    queryFn: async () =>
      (await api.GET("/ev/reports/customer-spend", { params: { query: query as never } })).data!,
  });
}
